import { useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginModal({ isOpen, onClose, onSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match for registration
    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/email/login' : '/auth/email/register';
      const response = await axios.post(`${API}${endpoint}`, {
        email: formData.email,
        password: formData.password,
        name: formData.name
      }, {
        withCredentials: true
      });

      // Store session token in localStorage for mobile Safari compatibility
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        console.log('Session token stored in localStorage');
      }

      toast.success(response.data.message);
      onSuccess(response.data.user);
      onClose();
      setFormData({ email: '', password: '', confirmPassword: '', name: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="cassette-card max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6B4423] hover:text-[#E85D4D]"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-3xl retro-title text-[#2C1810] mb-6">
          {mode === 'login' ? 'Welcome Back' : 'Create Account'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Name</label>
              <input
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-[#2C1810] mb-2">Email</label>
            <input
              type="email"
              data-testid="login-email-input"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-[#2C1810] mb-2">Password</label>
            <input
              type="password"
              data-testid="login-password-input"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
              minLength={6}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
            {mode === 'register' && (
              <p className="text-xs text-[#6B4423] mt-1">Minimum 6 characters</p>
            )}
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Confirm Password</label>
              <input
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                required
                minLength={6}
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="retro-button w-full"
          >
            {loading ? 'Please wait...' : (mode === 'login' ? 'Log In' : 'Create Account')}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-[#6B4423]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#F4E4C1] text-[#6B4423]">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-2 border-2 border-[#6B4423] rounded-lg bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-[#2C1810] font-medium">Google</span>
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[#6B4423] mb-2">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
          </p>
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="retro-button-small bg-[#E8934D] hover:bg-[#E85D4D]"
          >
            {mode === 'login' ? 'Create Account' : 'Back to Log In'}
          </button>
        </div>
      </div>
    </div>
  );
}
